import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      // Get token from localStorage if available
      const storedToken = localStorage.getItem('session_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else {
        // Clear invalid token
        localStorage.removeItem('session_token');
        setUser(null);
        return null;
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
      window.location.href = '/';
    }
  };

  const exchangeSession = async (sessionId) => {
    try {
      setError(null);
      
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      });


      if (!response.ok) {
        const errorData = await response.json();
        console.error('exchangeSession error:', errorData);
        throw new Error(errorData.detail || 'Oturum açılamadı');
      }

      const userData = await response.json();
      
      // Store session token in localStorage as backup
      if (userData.session_token) {
        localStorage.setItem('session_token', userData.session_token);
        // Verify it was saved
        const saved = localStorage.getItem('session_token');
      } else {
      }
      
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('exchangeSession exception:', err);
      setError(err.message);
      throw err;
    }
  };

  // Role helpers
  const hasRole = useCallback((roles) => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  }, [user]);

  const isAdmin = useCallback(() => {
    return hasRole(['tenant_owner', 'center_director']);
  }, [hasRole]);

  const canManageUsers = useCallback(() => {
    return hasRole(['tenant_owner', 'center_director']);
  }, [hasRole]);

  const canViewAllStudents = useCallback(() => {
    return hasRole(['tenant_owner', 'center_director', 'reading_coach']);
  }, [hasRole]);

  // Get session token for API calls
  const getToken = useCallback(() => {
    return localStorage.getItem('session_token');
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    exchangeSession,
    hasRole,
    isAdmin,
    canManageUsers,
    canViewAllStudents,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
